import React from 'react';
import { Alert, Button, InputNumber, Select, Space, Tooltip, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { DailyCardRarity, DailyCardSpec } from '@/types';
import {
  DAILY_CARD_RARITIES,
  DAILY_MAX_MILESTONE_CARDS,
  RARITY_LABELS,
  cardTotal,
  errorsAt,
  errorsUnder,
} from './dailyEngagementConfig';
import { fromNumber, toNumber, useDraft } from './draftContext';

const { Text } = Typography;

interface NumberInputProps {
  /** The document path, for this field's validation messages. */
  path: string;
  /**
   * Match the messages about this path or anything below it instead of `path`
   * exactly, for fields the backend reports as a group (`spin.respinSplit`,
   * `...reward.cards[k]`).
   */
  errorPrefix?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Whole numbers only (most fields); decimals allowed otherwise. */
  integer?: boolean;
  addonAfter?: React.ReactNode;
  width?: number;
  disabled?: boolean;
}

/** A number bound to one document path; red with its messages in a tooltip when invalid. */
export const NumberInput: React.FC<NumberInputProps> = ({
  path,
  errorPrefix,
  value,
  onChange,
  min,
  max,
  step,
  integer = true,
  addonAfter,
  width = 120,
  disabled,
}) => {
  const { editable, errors } = useDraft();
  const own = errorPrefix !== undefined ? errorsUnder(errors, errorPrefix) : errorsAt(errors, path);
  const input = (
    <InputNumber
      value={fromNumber(value)}
      min={min}
      max={max}
      step={step ?? 1}
      precision={integer ? 0 : undefined}
      addonAfter={addonAfter}
      status={own.length > 0 ? 'error' : undefined}
      disabled={!editable || disabled}
      style={{ width }}
      onChange={(v) => onChange(toNumber(v))}
    />
  );
  // Always wrapped, so the input is not remounted (and does not lose focus)
  // when a problem appears or goes away while the admin types.
  return <Tooltip title={own.length > 0 ? own.join('; ') : undefined}>{input}</Tooltip>;
};

interface LabeledNumberProps extends NumberInputProps {
  label: React.ReactNode;
  help?: React.ReactNode;
}

/** A labelled number with its help text, or its validation messages when invalid. */
export const LabeledNumber: React.FC<LabeledNumberProps> = ({ label, help, ...input }) => {
  const { errors } = useDraft();
  const own = errorsAt(errors, input.path);
  return (
    <Space direction="vertical" size={2} style={{ minWidth: 200 }}>
      <Text strong>{label}</Text>
      <NumberInput width={180} {...input} />
      {own.length > 0 ? (
        <Text type="danger" style={{ fontSize: 12 }}>
          {own.join('; ')}
        </Text>
      ) : (
        help && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {help}
          </Text>
        )
      )}
    </Space>
  );
};

interface ErrorListProps {
  /** Paths whose problems (and those below them) are listed. */
  prefixes: readonly string[];
  title?: string;
}

/** The client-side problems of one part of the document; nothing when it is valid. */
export const ErrorList: React.FC<ErrorListProps> = ({ prefixes, title }) => {
  const { errors } = useDraft();
  const shown = errors.filter((e) => prefixes.some((p) => errorsUnder([e], p).length > 0));
  if (shown.length === 0) return null;
  return (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 12 }}
      message={
        title ?? `${shown.length} problem${shown.length === 1 ? '' : 's'} to fix before saving`
      }
      description={
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {shown.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      }
    />
  );
};

interface CardListEditorProps {
  /** Path of the cards array, e.g. `days[2].cards`. */
  path: string;
  value: DailyCardSpec[];
  onChange: (cards: DailyCardSpec[]) => void;
  /** Label for an empty list. */
  emptyText?: string;
}

/** Rarity × count rows, at most 3 cards in total (the backend limit). */
export const CardListEditor: React.FC<CardListEditorProps> = ({
  path,
  value,
  onChange,
  emptyText = 'No cards',
}) => {
  const { editable } = useDraft();
  const total = cardTotal(value);
  const set = (i: number, card: DailyCardSpec) =>
    onChange(value.map((c, j) => (j === i ? card : c)));
  return (
    <Space direction="vertical" size={4}>
      {value.length === 0 && <Text type="secondary">{emptyText}</Text>}
      {value.map((card, i) => (
        <Space key={i} size={4}>
          <Select<DailyCardRarity>
            value={card.rarity}
            disabled={!editable}
            style={{ width: 120 }}
            onChange={(rarity) => set(i, { ...card, rarity })}
            options={DAILY_CARD_RARITIES.map((r) => ({ value: r, label: RARITY_LABELS[r] }))}
          />
          <NumberInput
            path={`${path}[${i}].count`}
            errorPrefix={`${path}[${i}]`}
            value={card.count}
            min={1}
            max={DAILY_MAX_MILESTONE_CARDS}
            width={70}
            onChange={(count) => set(i, { ...card, count })}
          />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={!editable}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            aria-label="Remove card"
          />
        </Space>
      ))}
      <Button
        size="small"
        type="dashed"
        icon={<PlusOutlined />}
        disabled={!editable || total >= DAILY_MAX_MILESTONE_CARDS}
        onClick={() => onChange([...value, { rarity: 'semi_pro', count: 1 }])}
      >
        Add card
      </Button>
    </Space>
  );
};
